/**
 * AdminSupport — /admin/support
 *
 * The async support queue: list tickets (filterable by status), open a thread,
 * reply (emails the user), and set status. No live chat.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  adminListSupportTickets,
  adminGetSupportTicket,
  adminReplySupportTicket,
  adminSetSupportStatus,
  type SupportTicketT,
} from '../../services/api';

interface AdminCtx {
  adminEmail: string | null;
}

const STATUS_FILTERS = ['open', 'awaiting_user', 'resolved', 'closed'] as const;

const STATUS_CLS: Record<string, string> = {
  open: 'bg-violet-100 text-violet-700',
  awaiting_user: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
};

function prettyCat(c: string) {
  return c.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function AdminSupport() {
  const { adminEmail } = useOutletContext<AdminCtx>();
  const [tickets, setTickets] = useState<SupportTicketT[]>([]);
  const [filter, setFilter] = useState<string>('open');
  const [active, setActive] = useState<SupportTicketT | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!adminEmail) return;
    try {
      const res = await adminListSupportTickets(adminEmail, filter || undefined);
      setTickets(res.data);
      setError(null);
    } catch {
      setError('Failed to load tickets.');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminEmail, filter]);

  const open = async (id: string) => {
    if (!adminEmail) return;
    const res = await adminGetSupportTicket(adminEmail, id);
    setActive(res.data);
  };

  const sendReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!adminEmail || !active || reply.trim().length < 1) return;
    setBusy(true);
    try {
      await adminReplySupportTicket(adminEmail, active.id, reply.trim());
      setReply('');
      await open(active.id);
      await load();
    } catch {
      setError('Failed to send reply.');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (s: string) => {
    if (!adminEmail || !active) return;
    setBusy(true);
    try {
      await adminSetSupportStatus(adminEmail, active.id, s);
      await open(active.id);
      await load();
    } catch {
      setError('Failed to update status.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Support</h1>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Queue */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex flex-wrap gap-2">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  filter === s ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {prettyCat(s)}
              </button>
            ))}
            <button
              onClick={() => setFilter('')}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                filter === '' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
          </div>

          {tickets.length === 0 ? (
            <p className="text-sm text-gray-400">No tickets.</p>
          ) : (
            <ul className="space-y-2">
              {tickets.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => open(t.id)}
                    className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-violet-300 ${
                      active?.id === t.id ? 'border-violet-400 bg-violet-50' : 'border-gray-200'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-gray-800">{t.subject}</span>
                      <span className="block truncate text-xs text-gray-400">
                        {t.user_email} · {prettyCat(t.category)}
                      </span>
                    </span>
                    <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_CLS[t.status] ?? ''}`}>
                      {prettyCat(t.status)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          {!active ? (
            <p className="text-sm text-gray-400">Select a ticket to view the conversation.</p>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="font-semibold text-gray-800">{active.subject}</h2>
                  <p className="text-xs text-gray-400">{active.user_email} · {prettyCat(active.category)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_CLS[active.status] ?? ''}`}>
                  {prettyCat(active.status)}
                </span>
              </div>

              <ul className="mt-4 max-h-72 space-y-2 overflow-y-auto">
                {active.messages?.map((m) => (
                  <li
                    key={m.id}
                    className={`rounded-lg px-3 py-2 text-sm ${
                      m.author === 'admin' ? 'bg-violet-50 border border-violet-100' : 'bg-gray-50'
                    }`}
                  >
                    <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {m.author === 'admin' ? `Support (${m.author_email ?? ''})` : 'Customer'}
                    </p>
                    <p className="whitespace-pre-wrap text-gray-700">{m.body}</p>
                  </li>
                ))}
              </ul>

              <form onSubmit={sendReply} className="mt-3">
                <textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  placeholder="Reply to the customer (emails them)…"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="submit"
                    disabled={busy || reply.trim().length < 1}
                    className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
                  >
                    {busy ? 'Sending…' : 'Send reply'}
                  </button>
                  <button type="button" onClick={() => setStatus('resolved')} disabled={busy}
                    className="rounded-lg bg-green-100 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-200 disabled:opacity-60">
                    Mark resolved
                  </button>
                  <button type="button" onClick={() => setStatus('closed')} disabled={busy}
                    className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-60">
                    Close
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
